import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Edit,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  Share2,
  Trash2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  createRewardCampaign,
  deleteRewardCampaign,
  getRewardCampaigns,
  updateRewardCampaign,
} from "../../services/api";

interface RewardCampaign {
  _id?: string;
  id?: string;
  amount?: number | string;
  expiresAt?: string;
  qrCode?: string;
  redeemUrl?: string;
  urlToken?: string;
  code?: string;
  createdAt?: string;
}

interface CampaignForm {
  amount: string;
  expiresAt: string;
}

const campaignsPerPage = 25;

const getDaysRemaining = (dateValue?: string) => {
  if (!dateValue) return null;

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(dateValue);

  if (Number.isNaN(expiry.getTime())) return null;

  const expiryStart = new Date(
    expiry.getFullYear(),
    expiry.getMonth(),
    expiry.getDate(),
  );

  const diffMs = expiryStart.getTime() - todayStart.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const getCampaignAmount = (campaign: RewardCampaign) => {
  const value = Number(campaign.amount);
  return Number.isNaN(value) ? 0 : value;
};

const toDateInputValue = (date?: string) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const day = `${parsed.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeCampaign = (item: any): RewardCampaign => {
  const base = item?.giftCard ? item.giftCard : item;
  return {
    ...base,
    _id: base?._id || base?.id,
    id: base?.id || base?._id,
    amount: base?.amount,
    expiresAt: base?.expiresAt,
    qrCode: base?.qrCode,
    redeemUrl: base?.redeemUrl,
    urlToken: base?.urlToken,
    code: base?.code || item?.code,
    createdAt: base?.createdAt,
  };
};

const extractCampaigns = (raw: any): RewardCampaign[] => {
  const source = raw?.data ?? raw;

  if (Array.isArray(source)) return source.map(normalizeCampaign);
  if (Array.isArray(source?.cards)) return source.cards.map(normalizeCampaign);
  if (Array.isArray(source?.giftCards)) return source.giftCards.map(normalizeCampaign);
  if (Array.isArray(source?.giftCard)) return source.giftCard.map(normalizeCampaign);
  if (source?.giftCard) return [normalizeCampaign(source)];

  return [];
};

const RewardCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<RewardCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedCampaign, setSelectedCampaign] = useState<RewardCampaign | null>(
    null,
  );
  const [qrCampaign, setQrCampaign] = useState<RewardCampaign | null>(null);
  const [formData, setFormData] = useState<CampaignForm>({
    amount: "",
    expiresAt: "",
  });

  const minDate = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = `${now.getMonth() + 1}`.padStart(2, "0");
    const day = `${now.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRewardCampaigns();
      setCampaigns(extractCampaigns(response?.data));
    } catch (err: any) {
      setCampaigns([]);
      const message =
        err?.response?.data?.error || "Failed to load reward campaigns";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const orderedCampaigns = useMemo(
    () =>
      [...campaigns].sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime(),
      ),
    [campaigns],
  );

  const filteredCampaigns = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orderedCampaigns;

    return orderedCampaigns.filter((campaign) => {
      const amount = getCampaignAmount(campaign).toFixed(2);
      const code = (campaign.code || "").toLowerCase();
      const id = (campaign._id || campaign.id || "").toLowerCase();
      const expiry = campaign.expiresAt
        ? new Date(campaign.expiresAt).toLocaleDateString().toLowerCase()
        : "";

      return (
        amount.includes(term) ||
        code.includes(term) ||
        id.includes(term) ||
        expiry.includes(term)
      );
    });
  }, [orderedCampaigns, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCampaigns.length / campaignsPerPage),
  );

  const paginatedCampaigns = useMemo(() => {
    const start = (currentPage - 1) * campaignsPerPage;
    return filteredCampaigns.slice(start, start + campaignsPerPage);
  }, [filteredCampaigns, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const resetForm = () => {
    setFormData({ amount: "", expiresAt: "" });
    setSelectedCampaign(null);
    setFormMode("create");
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    resetForm();
  };

  const openCreateModal = () => {
    setFormMode("create");
    setSelectedCampaign(null);
    setFormData({ amount: "", expiresAt: "" });
    setShowFormModal(true);
  };

  const openEditModal = (campaign: RewardCampaign) => {
    setFormMode("edit");
    setSelectedCampaign(campaign);
    setFormData({
      amount: `${getCampaignAmount(campaign)}`,
      expiresAt: toDateInputValue(campaign.expiresAt),
    });
    setShowFormModal(true);
  };

  const openQrDialog = (campaign: RewardCampaign) => {
    setQrCampaign(campaign);
  };

  const closeQrDialog = () => {
    setQrCampaign(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.amount || !formData.expiresAt) {
      toast.error("Amount and expiry date are required");
      return;
    }

    const amountNumber = Number(formData.amount);
    if (Number.isNaN(amountNumber) || amountNumber < 0) {
      toast.error("Amount must be a valid number greater than or equal to 0");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (formMode === "edit") {
        const campaignId = selectedCampaign?._id || selectedCampaign?.id;
        if (!campaignId) {
          toast.error("Campaign id is missing");
          return;
        }

        await updateRewardCampaign(campaignId, {
          amount: amountNumber,
          expiresAt: formData.expiresAt,
        });

        toast.success("Campaign updated successfully");
        closeFormModal();
        await fetchCampaigns();
        return;
      }

      const response = await createRewardCampaign({
        amount: amountNumber,
        expiresAt: formData.expiresAt,
      });
      const created = extractCampaigns(response?.data)[0];

      toast.success("Campaign created successfully");
      closeFormModal();
      await fetchCampaigns();

      if (created?.qrCode) {
        openQrDialog(created);
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        `Failed to ${formMode === "edit" ? "update" : "create"} campaign`;
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (campaign: RewardCampaign) => {
    const campaignId = campaign._id || campaign.id;
    if (!campaignId) {
      toast.error("Campaign id is missing");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this campaign? This action cannot be undone.",
    );
    if (!confirmDelete) return;

    try {
      setError(null);
      await deleteRewardCampaign(campaignId);
      toast.success("Campaign deleted successfully");
      await fetchCampaigns();
    } catch (err: any) {
      const message = err?.response?.data?.error || "Failed to delete campaign";
      setError(message);
      toast.error(message);
    }
  };

  const refreshData = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchCampaigns();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleShare = async () => {
    if (!qrCampaign) return;

    const shareUrl = qrCampaign.redeemUrl || "";

    try {
      if (navigator.share && shareUrl) {
        await navigator.share({
          title: "Gift Card",
          url: shareUrl,
        });
        return;
      }

      const fallbackText = shareUrl || qrCampaign.urlToken || qrCampaign.code;
      if (!fallbackText) {
        toast.error("Nothing available to share");
        return;
      }

      await navigator.clipboard.writeText(fallbackText);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Unable to share right now");
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="relative mx-auto flex h-screen max-w-7xl flex-col overflow-y-auto p-4 pt-0 no-scrollbar lg:p-8">
      <div className="sticky top-0 z-10 bg-white/90 py-6 backdrop-blur-sm">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-800">Reward Campaigns</h1>
          <button
            onClick={openCreateModal}
            className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap">
            <Plus size={18} />
            Add Campaign
          </button>
        </div>

        <div className="mb-6 rounded-lg">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Search by amount, code, id or expiry date
          </label>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search campaigns"
                className="w-full rounded-md border py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-5 py-2 text-white transition hover:bg-blue-700"
            >
              Search
            </button>
            <button
              type="button"
              onClick={refreshData}
              className="flex items-center justify-center rounded-full bg-blue-600 h-10 w-10 text-white transition hover:bg-blue-700"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
          </form>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-500 p-3 text-center text-white">{error}</div>
      )}

      <div className="relative flex-1">
        {loading && campaigns.length > 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
            <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-2 shadow">
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-blue-600" />
              <span className="text-sm text-gray-600">Loading...</span>
            </div>
          </div>
        )}

        {paginatedCampaigns.length === 0 ? (
          <div className="flex h-full items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No campaigns found</p>
              <p className="mt-1 text-sm">
                {searchTerm
                  ? "Try searching with a different value"
                  : "Create your first reward campaign"}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Expiry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedCampaigns.map((campaign, index) => {
                    const id = campaign._id || campaign.id || `campaign-${index}`;
                    const amount = getCampaignAmount(campaign);
                    const daysRemaining = getDaysRemaining(campaign.expiresAt);
                    const isExpired =
                      daysRemaining !== null && Number(daysRemaining) < 0;

                    return (
                      <tr key={id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-gray-900">
                          ${amount.toFixed(2)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          <span className="inline-flex items-center gap-2">
                            <CalendarDays size={14} className="text-gray-500" />
                            {campaign.expiresAt
                              ? new Date(campaign.expiresAt).toLocaleDateString()
                              : "No expiry"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              isExpired
                                ? "bg-red-100 text-red-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {daysRemaining === null
                              ? "No expiry"
                              : isExpired
                                ? "Expired"
                                : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          {campaign.createdAt
                            ? new Date(campaign.createdAt).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openQrDialog(campaign)}
                              className="rounded-full bg-blue-50 p-2 text-blue-700 transition hover:bg-blue-100"
                              title="View QR"
                            >
                              <QrCode size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openEditModal(campaign)}
                              className="rounded-full bg-amber-50 p-2 text-amber-700 transition hover:bg-amber-100"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(campaign)}
                              className="rounded-full bg-red-50 p-2 text-red-700 transition hover:bg-red-100"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
                <p className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-slate-900">
                {formMode === "edit" ? "Edit Campaign" : "Add Campaign"}
              </h2>
              <button
                onClick={closeFormModal}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Amount
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  placeholder="Enter amount"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  min={minDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, expiresAt: e.target.value }))
                  }
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeFormModal}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {saving
                    ? "Saving..."
                    : formMode === "edit"
                      ? "Update"
                      : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {qrCampaign && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Campaign QR</h2>
              <button
                onClick={closeQrDialog}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4">
              {qrCampaign.qrCode ? (
                <img
                  src={qrCampaign.qrCode}
                  alt="Gift card QR"
                  className="mx-auto h-64 w-64 rounded-xl border border-slate-200 bg-white p-3"
                />
              ) : (
                <div className="mx-auto grid h-64 w-64 place-content-center rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-500">
                  QR not available
                </div>
              )}

              <button
                type="button"
                onClick={handleShare}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <Share2 size={16} />
                Share
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardCampaigns;
